import { useMemo } from 'react';
import { SortingState } from '@tanstack/react-table';
import {
  useInfiniteMigrationInconsistenciesQuery,
  useMigrationInconsistencyEntityTypeCountsQuery,
} from '@bmwe/data/shared';
import { MigrationInconsistencyFragment } from '@bmwe/data/kwg/graphql';
import { BmweDataTable, EntityLink } from '../../components';
import { CommonView } from '../../views';
import {
  DATE_PRESETS,
  defaultLocale,
  mapToGraphQlFilterInput,
  toGraphQlSort,
  toReadableDateString,
} from '../../helper';
import { appManagementStrings } from '../../i18n';
import { BmweDataColumn } from '../../types';
import styles from './migrationInconsistenciesView.module.css';

const strings = appManagementStrings.migration;
const defaultSort: SortingState = [{ id: 'createdAt', desc: true }];
// CreatedAt is a real UTC instant (not a migrated date-only value), so it is
// shown in the viewer's local time instead of the preset's fixed UTC wall clock.
const createdAtFormat = { ...DATE_PRESETS.DATETIME, timeZone: undefined };

export function MigrationInconsistenciesView() {
  const { data: countsData } = useMigrationInconsistencyEntityTypeCountsQuery(
    {},
  );
  const counts = countsData?.migrationInconsistencyEntityTypeCounts;

  const columns = useMemo<BmweDataColumn<MigrationInconsistencyFragment>[]>(
    () => [
      {
        header: strings.inconsistency.createdAt,
        field: 'createdAt',
        sortable: true,
        filter: true,
        meta: { filterType: 'dateRange' },
        cell: ({ row }) =>
          toReadableDateString(
            row.original.createdAt,
            defaultLocale,
            createdAtFormat,
          ),
      },
      {
        header: strings.inconsistency.entityType,
        field: 'entityType',
        sortable: true,
        filter: true,
        meta: {
          filterType: 'select',
          filterOptions: counts?.map((count) => ({
            label: `${count.entityType} (${count.count})`,
            value: count.entityType,
          })),
        },
      },
      {
        header: strings.inconsistency.dossier,
        field: 'dossierName',
        sortable: true,
        filter: true,
        size: '1.5fr',
        cell: ({ row }) => (
          <EntityLink
            entity="DOSSIERS_VIEW"
            id={row.original.dossierId ?? undefined}
            name={row.original.dossierName ?? undefined}
          />
        ),
      },
      {
        header: strings.inconsistency.sharePointItemId,
        field: 'sharePointItemId',
        sortable: true,
        filter: true,
        meta: { filterType: 'numberRange', justify: 'end' },
      },
      {
        header: strings.inconsistency.errorMessage,
        field: 'errorMessage',
        filter: true,
        size: '3fr',
        cell: ({ row }) => (
          <span className={styles.message}>{row.original.errorMessage}</span>
        ),
      },
    ],
    [counts],
  );

  const total = counts?.reduce((sum, count) => sum + count.count, 0) ?? 0;

  return (
    <CommonView withBackButton title={strings.areaName}>
      <div className={styles.summary} role="list">
        <div className={styles.tile} role="listitem">
          <span className={styles.tileValue}>
            {total.toLocaleString(defaultLocale)}
          </span>
          <span className={styles.tileLabel}>{strings.total}</span>
        </div>
        {counts?.map((count) => (
          <div key={count.entityType} className={styles.tile} role="listitem">
            <span className={styles.tileValue}>
              {count.count.toLocaleString(defaultLocale)}
            </span>
            <span className={styles.tileLabel}>{count.entityType}</span>
          </div>
        ))}
      </div>
      <BmweDataTable
        columns={columns}
        tableKey="migration-inconsistencies-list"
        infiniteQueryHook={useInfiniteMigrationInconsistenciesQuery}
        pageSize={50}
        initialSort={defaultSort}
        entityLabel={strings.entityLabel}
        toTableQueryVariables={({
          sorting,
          filters,
          filterMode,
          pageSize,
        }) => ({
          order: toGraphQlSort(sorting),
          first: pageSize,
          where: mapToGraphQlFilterInput(filters, filterMode),
        })}
        mapToRows={(pages) =>
          pages.flatMap(
            (page) =>
              page.migrationInconsistencies?.edges?.map((edge) => edge.node) ??
              [],
          )
        }
        getPageInfo={(last) => last?.migrationInconsistencies?.pageInfo}
        getTotalCount={(last) =>
          last?.migrationInconsistencies?.totalCount ??
          last?.migrationInconsistencies?.edges?.length ??
          0
        }
      />
    </CommonView>
  );
}
